import React from 'react';
import { useSession } from '../../contexts/SessionContext';
import { Clock, ShieldAlert, Lock, CheckCircle2 } from 'lucide-react';
import Button from '../ui/Button';

const SessionWarningModal = () => {
  const { showWarning, countdownSeconds, extendSession, lockStation } = useSession();

  if (!showWarning) return null;

  // Calculate percentage of remaining countdown (out of 60 seconds)
  const totalDuration = 60;
  const progressPercent = Math.min(100, Math.max(0, (countdownSeconds / totalDuration) * 100));

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
      aria-describedby="session-warning-desc"
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in"
    >
      <div className="relative w-full max-w-md bg-card border-2 border-amber-500/40 rounded-2xl shadow-2xl p-6 sm:p-7 overflow-hidden text-card-foreground transform transition-all scale-100 animate-slide-up">
        {/* Amber glowing header banner */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />

        <div className="flex items-start gap-4 mb-4">
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
              <ShieldAlert className="w-7 h-7 animate-pulse" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-[10px] shadow">
              <Clock className="w-3.5 h-3.5" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-300 mb-1">
              Workstation Idle Warning
            </span>
            <h2 id="session-warning-title" className="text-lg font-bold text-foreground">
              Are you still working?
            </h2>
          </div>
        </div>

        <p id="session-warning-desc" className="text-sm text-muted-foreground leading-relaxed mb-6">
          No activity has been detected. To prevent unauthorized access to confidential patient health records, your workstation will lock in:
        </p>

        {/* Live Countdown Visual Indicator */}
        <div className="bg-muted/60 dark:bg-muted/30 border border-border rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Automatic Lock In
            </span>
            <span className="text-3xl font-extrabold font-mono text-amber-600 dark:text-amber-400">
              {countdownSeconds}s
            </span>
          </div>

          {/* Circular Countdown Ring */}
          <div className="relative w-14 h-14 flex items-center justify-center">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-muted/40 stroke-current"
                strokeWidth="3.5"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-amber-500 stroke-current transition-all duration-1000 ease-linear"
                strokeDasharray={`${progressPercent}, 100`}
                strokeLinecap="round"
                strokeWidth="3.5"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <Clock className="absolute w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3">
          <Button
            variant="outline"
            className="w-full sm:w-auto text-xs border-border hover:bg-muted"
            onClick={() => lockStation('manual')}
          >
            <Lock className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
            Lock Station Now
          </Button>

          <Button
            variant="primary"
            className="w-full sm:flex-1 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 shadow-md shadow-emerald-600/20"
            onClick={extendSession}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            I'm Still Here
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionWarningModal;
